import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import Header from 'components/ui/Header';
import Button from 'components/ui/Button';
import Icon from 'components/AppIcon';
import { serviceRequestService } from 'services/serviceRequestService';
import { supabase } from 'lib/supabase';

const ServiceRequestDetailPage = () => {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [request, setRequest] = useState(null);
  const [mediaUrls, setMediaUrls] = useState([]);
  const [mediaLoading, setMediaLoading] = useState(false);

  // Quotes state
  const [quotes, setQuotes] = useState([]);
  const [quotesLoading, setQuotesLoading] = useState(false);
  const [expandedTexts, setExpandedTexts] = useState({});
  const [shortlistLoading, setShortlistLoading] = useState({});
  const [toast, setToast] = useState(null);
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);
  const [completeLoading, setCompleteLoading] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [deactivateLoading, setDeactivateLoading] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [selectedQuoteId, setSelectedQuoteId] = useState(null);
  const [lightbox, setLightbox] = useState(null); // { photos: [{signed_url, original_filename}], currentIndex: number }

  // Review stats for accepted quotes only
  const [reviewStats, setReviewStats] = useState({});
  const [publicSlugs, setPublicSlugs] = useState({});

  // Lightbox keyboard navigation
  React.useEffect(() => {
    if (!lightbox) return;
    const handler = (e) => {
      if (e?.key === 'Escape') setLightbox(null);
      if (e?.key === 'ArrowLeft' && lightbox?.currentIndex > 0) {
        setLightbox(prev => ({ ...prev, currentIndex: prev?.currentIndex - 1 }));
      }
      if (e?.key === 'ArrowRight' && lightbox?.currentIndex < lightbox?.photos?.length - 1) {
        setLightbox(prev => ({ ...prev, currentIndex: prev?.currentIndex + 1 }));
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightbox]);

  // Validate UUID format
  const isValidUUID = (uuid) => {
    if (!uuid || typeof uuid !== 'string') return false;
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex?.test(uuid);
  };

  // Status label mapping
  const getStatusLabel = (status) => {
    const labels = {
      pending: 'In Attesa',
      active: 'Attiva',
      accepted: 'Accettata',
      in_progress: 'In Corso',
      completed: 'Completata',
      cancelled: 'Annullata'
    };
    return labels?.[status] || status;
  };

  // Status color mapping
  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-50 text-yellow-700 border-yellow-200',
      active: 'bg-blue-50 text-blue-700 border-blue-200',
      accepted: 'bg-green-50 text-green-700 border-green-200',
      in_progress: 'bg-blue-50 text-blue-700 border-blue-200',
      completed: 'bg-green-50 text-green-700 border-green-200',
      cancelled: 'bg-red-50 text-red-700 border-red-200'
    };
    return colors?.[status] || 'bg-gray-50 text-gray-700 border-gray-200';
  };

  // Urgency label mapping
  const getUrgencyLabel = (urgency) => {
    const labels = {
      low: 'Bassa',
      medium: 'Media',
      high: 'Alta',
      urgent: 'Urgente'
    };
    return labels?.[urgency] || urgency;
  };

  // Urgency color mapping
  const getUrgencyColor = (urgency) => {
    const colors = {
      low: 'bg-green-50 text-green-700 border-green-200',
      medium: 'bg-yellow-50 text-yellow-700 border-yellow-200',
      high: 'bg-orange-50 text-orange-700 border-orange-200',
      urgent: 'bg-red-50 text-red-700 border-red-200'
    };
    return colors?.[urgency] || 'bg-gray-50 text-gray-700 border-gray-200';
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date?.toLocaleDateString('it-IT', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'N/A';
    }
  };

  // Calculate active request duration and remaining days
  const calculateActiveDuration = (createdAt, status) => {
    if (status !== 'active' || !createdAt) return null;
    
    try {
      const today = new Date();
      const creationDate = new Date(createdAt);
      const diffTime = Math.abs(today - creationDate);
      const daysSinceCreation = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      const remainingDays = 30 - daysSinceCreation;
      
      // Only return if there are remaining days
      if (remainingDays <= 0) return null;
      
      return {
        daysSinceCreation,
        remainingDays
      };
    } catch {
      return null;
    }
  };

  // Check if file is previewable image format
  const isPreviewableImage = (filename) => {
    if (!filename) return false;
    const ext = filename?.toLowerCase()?.split('.')?.pop();
    return ['jpg', 'jpeg', 'png', 'webp', 'gif']?.includes(ext);
  };

  // Toast helper
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  // Fetch quotes
  const fetchQuotes = useCallback(async () => {
    if (!requestId) return;
    setQuotesLoading(true);
    try {
      const { data, error: qErr } = await supabase?.from('quotes')?.select('*, users(verification_status, email)')?.eq('service_request_id', requestId)?.order('preview_price', { ascending: true });
      if (qErr) {
        console.error('Error fetching quotes:', qErr);
      } else {
        setQuotes(data || []);
      }
    } catch (err) {
      console.error('Error fetching quotes:', err);
    } finally {
      setQuotesLoading(false);
    }
  }, [requestId]);

  // Shortlist count
  const shortlistedCount = quotes?.filter(q => q?.status === 'shortlisted' || q?.status === 'accepted')?.length || 0;

  // Has at least one accepted (unlocked) quote
  const hasAccepted = quotes?.some(q => q?.status === 'accepted');

  // Toggle expanded text
  const toggleExpanded = (quoteId) => {
    setExpandedTexts(prev => ({ ...prev, [quoteId]: !prev?.[quoteId] }));
  };

  // Add to shortlist
  const handleAddToShortlist = async (quoteId) => {
    if (shortlistedCount >= 3) {
      showToast('Hai già selezionato 3 preventivi', 'error');
      return;
    }
    setShortlistLoading(prev => ({ ...prev, [quoteId]: true }));
    try {
      const { error: upErr } = await supabase?.from('quotes')?.update({ status: 'shortlisted', shortlisted_at: new Date()?.toISOString() })?.eq('id', quoteId);
      if (upErr) {
        showToast('Errore durante l\'aggiornamento', 'error');
      } else {
        await fetchQuotes();
        showToast('Preventivo selezionato');
      }
    } catch (err) {
      showToast('Errore durante l\'aggiornamento', 'error');
    } finally {
      setShortlistLoading(prev => ({ ...prev, [quoteId]: false }));
    }
  };

  // Remove from shortlist
  const handleRemoveFromShortlist = async (quoteId) => {
    setShortlistLoading(prev => ({ ...prev, [quoteId]: true }));
    try {
      const { error: upErr } = await supabase?.from('quotes')?.update({ status: 'sent', shortlisted_at: null })?.eq('id', quoteId);
      if (upErr) {
        showToast('Errore durante l\'aggiornamento', 'error');
      } else {
        await fetchQuotes();
        showToast('Rimosso dalla selezione');
      }
    } catch (err) {
      showToast('Errore durante l\'aggiornamento', 'error');
    } finally {
      setShortlistLoading(prev => ({ ...prev, [quoteId]: false }));
    }
  };

  const handleAcceptQuote = async (quoteId) => {
    setShortlistLoading(prev => ({ ...prev, [quoteId]: true }));
    try {
      const { error } = await supabase?.from('quotes')?.update({ status: 'accepted', shortlisted_at: new Date()?.toISOString() })?.eq('id', quoteId);
      if (error) { showToast('Errore durante l\'accettazione', 'error'); }
      else {
        await fetchQuotes();
        showToast('Contatto sbloccato');
        const quote = quotes?.find(q => q?.id === quoteId);
        const { data: profData } = await supabase?.from('users')?.select('email')?.eq('id', quote?.professional_id)?.single();
        supabase?.functions?.invoke('notify-quote-unlocked', {
          body: { professionalEmail: profData?.email, requestTitle: request?.title }
        })?.catch(err => console.log('notify-quote-unlocked error:', err));
      }
    } catch { showToast('Errore durante l\'accettazione', 'error'); }
    finally { setShortlistLoading(prev => ({ ...prev, [quoteId]: false })); }
  };

  // Handle deactivate request
  const handleDeactivate = async () => {
    setDeactivateLoading(true);
    try {
      const { error: reqErr } = await supabase
        ?.from('service_requests')
        ?.update({ status: 'archived' })
        ?.eq('id', requestId);
      if (reqErr) {
        showToast('Errore durante la disattivazione della richiesta', 'error');
        setDeactivateLoading(false);
        return;
      }
      const { error: matchErr } = await supabase
        ?.from('request_matches')
        ?.update({ status: 'client_cancelled' })
        ?.eq('request_id', requestId);
      if (matchErr) {
        console.error('Error updating request_matches:', matchErr);
      }
      setShowDeactivateModal(false);
      navigate('/cliente/dashboard');
    } catch (err) {
      showToast('Errore durante la disattivazione della richiesta', 'error');
      setDeactivateLoading(false);
    }
  };

  // Handle complete request
  const handleCompleteRequest = async () => {
    setCompleteLoading(true);
    try {
      // Step 1: Mark request as completed
      const { error: upErr } = await supabase?.from('service_requests')?.update({ status: 'completed' })?.eq('id', requestId);
      if (upErr) {
        showToast('Errore durante l\'aggiornamento della richiesta', 'error');
        setCompleteLoading(false);
        return;
      }

      // Case B: "Nessuno di questi" selected
      if (selectedQuoteId === 'none' || selectedQuoteId === 'external') {
        setShowCompleteModal(false);
        showToast('Richiesta completata e archiviata');
        navigate('/cliente/dashboard');
        return;
      }

      // Case A: A professional quote was selected — call Edge Function
      const { data: fnData, error: fnErr } = await supabase?.functions?.invoke('create-review-invitation', {
        body: { quote_id: selectedQuoteId }
      });

      if (fnErr || !fnData?.token) {
        showToast('Errore nella creazione della recensione', 'error');
        setCompleteLoading(false);
        return;
      }

      setShowCompleteModal(false);
      navigate(`/review/${fnData?.token}`);
    } catch (err) {
      showToast('Errore durante l\'aggiornamento della richiesta', 'error');
      setCompleteLoading(false);
    }
  };

  // Fetch request data
  useEffect(() => {
    const fetchRequest = async () => {
      if (authLoading) return;

      if (!user) {
        navigate('/login-form?returnTo=/cliente/dashboard');
        return;
      }

      // Validate requestId is a valid UUID
      if (!isValidUUID(requestId)) {
        setError('ID richiesta non valido');
        setLoading(false);
        setTimeout(() => {
          navigate('/cliente/dashboard');
        }, 2000);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const { data, error: fetchError } = await serviceRequestService?.fetchClientRequestById(requestId);

        if (fetchError) {
          setError(fetchError);
          setRequest(null);
        } else if (data) {
          setRequest(data);
          
          // Fetch media URLs if media files exist
          if (data?.media_files && data?.media_files?.length > 0) {
            setMediaLoading(true);
            const { success, urls, error: mediaError } = await serviceRequestService?.getServiceRequestMediaUrls(data?.media_files);
            
            if (success && urls) {
              setMediaUrls(urls);
            } else if (mediaError) {
              console.error('Failed to load media URLs:', mediaError);
            }
            setMediaLoading(false);
          }
        } else {
          setError('Richiesta non trovata');
        }
      } catch (err) {
        console.error('Error fetching request:', err);
        setError('Si è verificato un errore durante il caricamento della richiesta');
      } finally {
        setLoading(false);
      }
    };

    fetchRequest();
  }, [requestId, user, authLoading, navigate]);

  // Fetch quotes after request loads
  useEffect(() => {
    if (!loading && request) {
      fetchQuotes();
    }
  }, [loading, request, fetchQuotes]);

  // Fetch review stats and public slugs for accepted quotes only
  useEffect(() => {
    if (!quotes || quotes?.length === 0) return;
    const profIds = [...new Set(
      quotes
        .map(q => q.professional_id)
        .filter(Boolean)
    )];
    if (profIds?.length === 0) return;

    const fetchReviewData = async () => {
      try {
        const [statsRes, slugsRes] = await Promise.all([
          supabase?.rpc('get_review_stats_for_professionals', { p_user_ids: profIds }),
          supabase?.rpc('get_public_slugs_for_professionals', { p_user_ids: profIds })
        ]);

        const statsMap = {};
        (statsRes?.data || [])?.forEach(r => {
          statsMap[r.professional_id] = {
            avg: parseFloat(r?.avg_rating),
            count: parseInt(r?.total_reviews)
          };
        });
        const slugMap = {};
        (slugsRes?.data || [])?.forEach(r => { slugMap[r.user_id] = r?.slug; });

        setReviewStats(statsMap);
        setPublicSlugs(slugMap);
      } catch (err) {
        console.error('Error fetching review stats:', err);
      }
    };

    fetchReviewData();
  }, [quotes]);

  // Loading state
  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="card text-center py-12">
            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Caricamento...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="card text-center py-12">
            <Icon name="AlertCircle" size={48} color="#dc2626" className="mx-auto mb-4" />
            <h2 className="text-xl font-bold text-foreground mb-2">Errore</h2>
            <p className="text-muted-foreground mb-6">{error}</p>
            <Button onClick={() => navigate('/cliente/dashboard')} iconName="ArrowLeft">
              Torna alla Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // No request found
  if (!request) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="card text-center py-12">
            <Icon name="FileQuestion" size={48} color="#6b7280" className="mx-auto mb-4" />
            <h2 className="text-xl font-bold text-foreground mb-2">Richiesta non trovata</h2>
            <p className="text-muted-foreground mb-6">La richiesta che stai cercando non esiste o non hai i permessi per visualizzarla.</p>
            <Button onClick={() => navigate('/cliente/dashboard')} iconName="ArrowLeft">
              Torna alla Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Breadcrumb */}
        <nav className="mb-6">
          <ol className="flex items-center gap-2 text-sm">
            <li>
              <Link to="/cliente/dashboard" className="text-primary hover:underline flex items-center gap-1">
                <Icon name="Home" size={16} />
                Dashboard
              </Link>
            </li>
            <li className="text-muted-foreground">/</li>
            <li className="text-foreground font-medium">Dettaglio Richiesta</li>
          </ol>
        </nav>

        {/* Header Section */}
        <div className="card mb-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-foreground mb-2">{request?.title}</h1>
              <p className="text-sm text-muted-foreground">ID: {request?.id}</p>
            </div>
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-semibold border ${getStatusColor(request?.status)}`}>
                  {getStatusLabel(request?.status)}
                </span>
                {request?.status === 'active' && (
                  <button
                    onClick={() => setShowDeactivateModal(true)}
                    className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-semibold border bg-white"
                    style={{ borderColor: '#dc2626', color: '#dc2626' }}
                  >
                    Disattiva richiesta
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Main Information */}
        <div className="card mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Icon name="FileText" size={20} color="var(--color-primary)" />
            </div>
            <h2 className="text-xl font-semibold text-foreground">Descrizione</h2>
          </div>
          <p className="text-foreground leading-relaxed whitespace-pre-wrap">{request?.description}</p>
        </div>

        {/* Media Gallery Section */}
        {mediaUrls?.length > 0 && (
          <div className="card mb-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Icon name="Image" size={20} color="#a855f7" />
              </div>
              <h2 className="text-xl font-semibold text-foreground">Foto e Immagini</h2>
            </div>
            
            {mediaLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                <p className="ml-3 text-muted-foreground">Caricamento immagini...</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Previewable Images Gallery */}
                {mediaUrls?.filter(file => file?.previewable)?.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground mb-3">Anteprima Immagini</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                      {mediaUrls?.filter(file => file?.previewable)?.map((file, index, arr) => (
                        <div
                          key={index}
                          className="relative group border border-gray-200 rounded-lg overflow-hidden aspect-square cursor-pointer hover:opacity-90 transition-opacity"
                          onClick={() => setLightbox({ photos: arr, currentIndex: index })}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => { if (e?.key === 'Enter') setLightbox({ photos: arr, currentIndex: index }); }}
                        >
                          <img
                            src={file?.signed_url}
                            alt={file?.original_filename || `Image ${index + 1}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.src = '/assets/images/no_image.png';
                              e.target.alt = 'Immagine non disponibile';
                            }}
                          />
                          <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs p-2 truncate">
                            {file?.original_filename}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Non-Previewable Files (Download Only) */}
                {mediaUrls?.filter(file => !file?.previewable)?.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground mb-3">File da Scaricare</h3>
                    <div className="space-y-2">
                      {mediaUrls?.filter(file => !file?.previewable)?.map((file, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                              <Icon name="FileImage" size={20} color="var(--color-primary)" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-foreground truncate">{file?.original_filename}</p>
                              <p className="text-xs text-muted-foreground">{file?.mime_type}</p>
                            </div>
                          </div>
                          <a
                            href={file?.signed_url}
                            download={file?.original_filename}
                            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors flex-shrink-0"
                          >
                            <Icon name="Download" size={16} />
                            <span className="text-sm font-medium">Scarica</span>
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Metadata Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Service Type */}
          <div className="card">
            <div className="flex items-center gap-3 mb-2">
              <Icon name="Briefcase" size={20} color="var(--color-primary)" />
              <h3 className="text-sm font-medium text-muted-foreground">Tipo di Servizio</h3>
            </div>
            <p className="text-lg font-semibold text-foreground">{request?.service_type || 'N/A'}</p>
          </div>

          {/* Urgency */}
          <div className="card">
            <div className="flex items-center gap-3 mb-2">
              <Icon name="AlertCircle" size={20} color="var(--color-primary)" />
              <h3 className="text-sm font-medium text-muted-foreground">Urgenza</h3>
            </div>
            <span className={`inline-flex items-center px-3 py-1 rounded-lg text-sm font-semibold border ${getUrgencyColor(request?.urgency)}`}>
              {getUrgencyLabel(request?.urgency)}
            </span>
          </div>

          {/* Creation Date */}
          <div className="card">
            <div className="flex items-center gap-3 mb-2">
              <Icon name="Calendar" size={20} color="var(--color-primary)" />
              <h3 className="text-sm font-medium text-muted-foreground">Data Creazione</h3>
            </div>
            <p className="text-lg font-semibold text-foreground">{formatDate(request?.created_at)}</p>
            {(() => {
              const activeDuration = calculateActiveDuration(request?.created_at, request?.status);
              if (activeDuration) {
                return (
                  <div className="mt-2 text-sm text-muted-foreground">
                    <p>Attiva da {activeDuration?.daysSinceCreation} {activeDuration?.daysSinceCreation === 1 ? 'giorno' : 'giorni'}</p>
                    <p>Scade tra {activeDuration?.remainingDays} {activeDuration?.remainingDays === 1 ? 'giorno' : 'giorni'}</p>
                  </div>
                );
              }
              return null;
            })()}
          </div>

          {/* Location */}
          <div className="card">
            <div className="flex items-center gap-3 mb-2">
              <Icon name="MapPin" size={20} color="var(--color-primary)" />
              <h3 className="text-sm font-medium text-muted-foreground">Località</h3>
            </div>
            <p className="text-lg font-semibold text-foreground">{request?.location_name || 'N/A'}</p>
            {request?.location_full_name && (
              <p className="text-sm text-muted-foreground mt-1">{request?.location_full_name}</p>
            )}
          </div>
        </div>

        {/* Estimated Compensation */}
        {request?.estimated_compensation && (
          <div className="card mb-6">
            <div className="flex items-center gap-3 mb-2">
              <Icon name="Euro" size={20} color="var(--color-primary)" />
              <h3 className="text-sm font-medium text-muted-foreground">Compenso Stimato</h3>
            </div>
            <p className="text-2xl font-bold text-foreground">€ {parseFloat(request?.estimated_compensation)?.toFixed(2)}</p>
          </div>
        )}

        {/* Response Deadline */}
        {request?.response_deadline && (
          <div className="card mb-6">
            <div className="flex items-center gap-3 mb-2">
              <Icon name="Clock" size={20} color="var(--color-primary)" />
              <h3 className="text-sm font-medium text-muted-foreground">Scadenza Risposta</h3>
            </div>
            <p className="text-lg font-semibold text-foreground">{formatDate(request?.response_deadline)}</p>
          </div>
        )}

        {/* Preventivi ricevuti */}
        <div className="card mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Icon name="FileCheck" size={20} color="#16a34a" />
            </div>
            <h2 className="text-xl font-semibold text-foreground">Preventivi ricevuti</h2>
          </div>

          {/* Preventivi selezionati */}
          <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg w-fit">
            <Icon name="Users" size={16} color="#2563eb" />
            <span className="text-sm font-medium text-blue-700">
              Preventivi selezionati: {shortlistedCount}/3
            </span>
          </div>

          {quotesLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              <p className="ml-3 text-muted-foreground text-sm">Caricamento preventivi...</p>
            </div>
          ) : quotes?.length === 0 ? (
            <div className="text-center py-8">
              <Icon name="Inbox" size={40} color="#9ca3af" className="mx-auto mb-3" />
              <p className="text-muted-foreground text-sm">Nessun preventivo ricevuto ancora.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {quotes?.map((quote) => {
                const isExpanded = expandedTexts?.[quote?.id];
                const previewText = quote?.preview_text || '';
                const truncated = previewText?.length > 120 && !isExpanded;
                const displayText = truncated ? previewText?.slice(0, 120) + '…' : previewText;
                const isShortlisted = quote?.status === 'shortlisted';
                const isAccepted = quote?.status === 'accepted';
                const isVerified = quote?.users?.verification_status === 'approved';
                const isLoadingThis = shortlistLoading?.[quote?.id];
                const stats = reviewStats?.[quote?.professional_id];
                const hasEnoughReviews = stats && stats?.count >= 3;
                const slug = publicSlugs?.[quote?.professional_id];

                return (
                  <div
                    key={quote?.id}
                    className={`border rounded-xl p-4 transition-all ${isAccepted ? 'border-green-400 bg-green-50' : isShortlisted ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-white'}`}
                  >
                    {/* Top row: price + badges */}
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <span className="text-xl font-bold text-foreground">
                        € {parseFloat(quote?.preview_price || 0)?.toFixed(2)}
                      </span>
                      <div className="flex items-center gap-2 ml-auto flex-wrap">
                        {isVerified && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                            ✓ Verificato
                          </span>
                        )}
                        {isAccepted ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700 border border-green-400">
                            Contatto sbloccato ✓
                          </span>
                        ) : isShortlisted ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700 border border-green-300">
                            Selezionato
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600 border border-gray-300">
                            Ricevuto
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Reputation row — visible on ALL cards */}
                    <div className="flex items-center gap-2 mt-1 mb-2">
                      {hasEnoughReviews ? (
                        <>
                          <svg width="16" height="16" viewBox="0 0 20 20" fill="#fbbf24" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                          <span className="text-sm font-bold text-foreground">{stats?.avg?.toFixed(1)}</span>
                          <span className="text-xs text-gray-500">
                            ({stats?.count === 1 ? '1 recensione' : `${stats?.count} recensioni`})
                          </span>
                          {slug && (
                            <a
                              href={`/p/${slug}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-primary font-medium hover:underline ml-1"
                            >
                              Vedi profilo
                            </a>
                          )}
                        </>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                          Nuovo professionista
                        </span>
                      )}
                    </div>
                    {/* Preview text */}
                    {previewText ? (
                      <div className="mb-3">
                        <p className="text-sm text-foreground leading-relaxed">{displayText}</p>
                        {previewText?.length > 120 && (
                          <button
                            onClick={() => toggleExpanded(quote?.id)}
                            className="text-xs text-primary font-medium mt-1 hover:underline focus:outline-none"
                          >
                            {isExpanded ? 'Mostra meno' : 'Leggi di più'}
                          </button>
                        )}
                      </div>
                    ) : null}
                    {/* Contact info box (only when accepted) */}
                    {isAccepted && (
                      <div className="mb-3 p-3 bg-green-100 border border-green-300 rounded-lg">
                        <p className="text-xs font-semibold text-green-800 mb-1">Contatto sbloccato</p>
                        <p className="text-sm text-green-900 font-medium">{quote?.users?.email}</p>
                        {quote?.pdf_url && (
                          <a
                            href={quote?.pdf_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 text-xs font-medium rounded-lg border border-green-400 bg-white text-green-700 hover:bg-green-50 transition-colors"
                          >
                            <Icon name="FileText" size={14} />
                            Visualizza PDF
                          </a>
                        )}
                        {/* Vedi profilo button (no review block here — moved to top) */}
                        {publicSlugs?.[quote?.professional_id] && (
                          <div className="mt-3 pt-3 border-t border-green-200">
                            <button
                              onClick={() => window.open(`/p/${publicSlugs?.[quote?.professional_id]}`, '_blank')}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-green-400 bg-white text-green-700 hover:bg-green-50 transition-colors"
                            >
                              <Icon name="ExternalLink" size={14} />
                              Vedi profilo
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                    {/* Actions row */}
                    <div className="flex flex-wrap items-center gap-2 mt-3">
                      {/* PDF button (disabled, shown only when not accepted) */}
                      {quote?.pdf_url && !isAccepted && (
                        <div className="relative group">
                          <button
                            disabled
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed"
                          >
                            <Icon name="FileText" size={14} />
                            Visualizza PDF
                          </button>
                          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                            Disponibile dopo accettazione
                          </div>
                        </div>
                      )}

                      {/* Shortlist / Accept buttons */}
                      {isAccepted ? null : isShortlisted ? (
                        <div className="flex items-center gap-2 ml-auto flex-wrap">
                          <button
                            onClick={() => handleAcceptQuote(quote?.id)}
                            disabled={isLoadingThis}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-green-500 bg-green-600 text-white hover:bg-green-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                          >
                            {isLoadingThis ? (
                              <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                              <Icon name="CheckCircle" size={14} />
                            )}
                            Sblocca contatto
                          </button>
                          <button
                            onClick={() => handleRemoveFromShortlist(quote?.id)}
                            disabled={isLoadingThis}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-red-300 bg-red-50 text-red-600 hover:bg-red-100 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                          >
                            {isLoadingThis ? (
                              <div className="w-3 h-3 border-2 border-red-400 border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                              <Icon name="X" size={14} />
                            )}
                            Rimuovi dalla selezione
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleAddToShortlist(quote?.id)}
                          disabled={isLoadingThis || shortlistedCount >= 3}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-green-300 bg-green-50 text-green-700 hover:bg-green-100 transition-colors disabled:opacity-60 disabled:cursor-not-allowed ml-auto"
                        >
                          {isLoadingThis ? (
                            <div className="w-3 h-3 border-2 border-green-500 border-t-transparent rounded-full animate-spin"></div>
                          ) : (
                            <Icon name="Plus" size={14} />
                          )}
                          Seleziona
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Back Button */}
        <div className="flex justify-center">
          {/* Complete Request Button */}
          {request?.status === 'active' && hasAccepted && (
            <div className="w-full mb-6">
              <div className="flex justify-center">
                <button
                  onClick={() => { setSelectedQuoteId(null); setShowCompleteModal(true); }}
                  className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white font-semibold rounded-xl hover:bg-green-700 transition-colors shadow-sm"
                >
                  <Icon name="CheckCircle" size={20} />
                  Segna come completata
                </button>
              </div>
            </div>
          )}
          <Button onClick={() => navigate('/cliente/dashboard')} iconName="ArrowLeft" variant="outline" size="lg">
            Torna alla Dashboard
          </Button>
        </div>
      </div>
      {/* Toast notification */}
      {toast && (
        <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg text-white text-sm font-medium transition-all ${toast?.type === 'error' ? 'bg-red-600' : 'bg-green-600'}`}>
          <Icon name={toast?.type === 'error' ? 'AlertCircle' : 'CheckCircle'} size={18} />
          {toast?.message}
        </div>
      )}
      {/* Deactivate Confirmation Modal */}
      {showDeactivateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <h2 className="text-lg font-bold text-foreground mb-3">Disattiva richiesta</h2>
            <p className="text-sm text-muted-foreground mb-6">
              Sei sicuro di voler disattivare questa richiesta? I professionisti non potranno più inviarti preventivi.
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setShowDeactivateModal(false)}
                disabled={deactivateLoading}
                className="px-5 py-2.5 border border-gray-300 bg-white text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Annulla
              </button>
              <button
                onClick={handleDeactivate}
                disabled={deactivateLoading}
                className="flex items-center gap-2 px-5 py-2.5 font-semibold rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed text-white"
                style={{ backgroundColor: '#dc2626' }}
              >
                {deactivateLoading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : null}
                Disattiva
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Professional Selection Modal */}
      {showCompleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="mb-5">
              <h2 className="text-xl font-bold text-foreground mb-1">Quale professionista ha eseguito il lavoro?</h2>
              <p className="text-sm text-muted-foreground">Riceverai un link per lasciare una recensione.</p>
            </div>

            {/* Accepted quotes list */}
            <div className="space-y-3 mb-4">
              {quotes?.filter(q => q?.status === 'accepted')?.map((quote) => {
                const isSelected = selectedQuoteId === quote?.id;
                return (
                  <button
                    key={quote?.id}
                    onClick={() => setSelectedQuoteId(quote?.id)}
                    className={`w-full text-left p-4 rounded-xl border-2 transition-all flex items-center gap-4 ${
                      isSelected
                        ? 'border-[#32b3e5] bg-blue-50'
                        : 'border-gray-200 bg-white hover:border-[#32b3e5]/50 hover:bg-blue-50/30'
                    }`}
                  >
                    {/* Radio indicator */}
                    <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-all ${
                      isSelected ? 'border-[#32b3e5] bg-[#32b3e5]' : 'border-gray-300 bg-white'
                    }`}>
                      {isSelected && <div className="w-2 h-2 rounded-full bg-white"></div>}
                    </div>
                    {/* Quote info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-foreground truncate">{quote?.users?.email || 'Professionista'}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Preventivo: <span className="font-medium text-foreground">€{parseFloat(quote?.preview_price || 0)?.toFixed(2)}</span>
                      </p>
                    </div>
                  </button>
                );
              })}

              {/* "Nessuno di questi" option */}
              <button
                onClick={() => setSelectedQuoteId('none')}
                className={`w-full text-left p-4 rounded-xl border-2 transition-all flex items-center gap-4 ${
                  selectedQuoteId === 'none' ?'border-[#32b3e5] bg-blue-50' :'border-gray-200 bg-white hover:border-[#32b3e5]/50 hover:bg-blue-50/30'
                }`}
              >
                <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-all ${
                  selectedQuoteId === 'none' ? 'border-[#32b3e5] bg-[#32b3e5]' : 'border-gray-300 bg-white'
                }`}>
                  {selectedQuoteId === 'none' && <div className="w-2 h-2 rounded-full bg-white"></div>}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground">Nessuno di questi (chiudi senza recensione)</p>
                </div>
              </button>

              {/* "Ho scelto un professionista fuori da Volaptic" option */}
              <button
                onClick={() => setSelectedQuoteId('external')}
                className={`w-full text-left p-4 rounded-xl border-2 transition-all flex items-center gap-4 ${
                  selectedQuoteId === 'external' ? 'border-[#32b3e5] bg-blue-50' : 'border-gray-200 bg-white hover:border-[#32b3e5]/50 hover:bg-blue-50/30'
                }`}
              >
                <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-all ${
                  selectedQuoteId === 'external' ? 'border-[#32b3e5] bg-[#32b3e5]' : 'border-gray-300 bg-white'
                }`}>
                  {selectedQuoteId === 'external' && <div className="w-2 h-2 rounded-full bg-white"></div>}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground">Ho scelto un professionista fuori da Volaptic</p>
                  <p className="text-xs text-gray-400 mt-0.5">Verrà archiviata senza recensione</p>
                </div>
              </button>
            </div>

            {/* Action buttons */}
            <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-100">
              <button
                onClick={() => { setShowCompleteModal(false); setSelectedQuoteId(null); }}
                disabled={completeLoading}
                className="px-5 py-2.5 border border-gray-300 bg-white text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Annulla
              </button>
              <button
                onClick={handleCompleteRequest}
                disabled={!selectedQuoteId || completeLoading}
                className="flex items-center gap-2 px-5 py-2.5 font-semibold rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed text-white"
                style={{ backgroundColor: selectedQuoteId && !completeLoading ? '#2780b5' : undefined, background: (!selectedQuoteId || completeLoading) ? undefined : 'linear-gradient(135deg, #32b3e5, #2780b5)' }}
              >
                {completeLoading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <Icon name="CheckCircle" size={16} />
                )}
                {selectedQuoteId === 'none' ? 'Conferma' : selectedQuoteId === 'external' ? 'Conferma e archivia' : 'Conferma e lascia recensione'}
              </button>
            </div>
          </div>
        </div>
      )}
      {lightbox && (
        <div
          onClick={() => setLightbox(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <div onClick={(e) => e?.stopPropagation()} style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {lightbox?.currentIndex > 0 && (
              <button
                onClick={(e) => { e?.stopPropagation(); setLightbox(prev => ({ ...prev, currentIndex: prev?.currentIndex - 1 })); }}
                style={{ position: 'absolute', left: '-48px', top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', fontSize: '2.5rem', width: '40px', height: '40px', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}
                aria-label="Foto precedente"
              >‹</button>
            )}
            <img
              src={lightbox?.photos?.[lightbox?.currentIndex]?.signed_url}
              alt={lightbox?.photos?.[lightbox?.currentIndex]?.original_filename || 'Foto'}
              style={{ maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain', borderRadius: '8px', boxShadow: '0 8px 40px rgba(0,0,0,0.6)' }}
            />
            {lightbox?.currentIndex < lightbox?.photos?.length - 1 && (
              <button
                onClick={(e) => { e?.stopPropagation(); setLightbox(prev => ({ ...prev, currentIndex: prev?.currentIndex + 1 })); }}
                style={{ position: 'absolute', right: '-48px', top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', fontSize: '2.5rem', width: '40px', height: '40px', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}
                aria-label="Foto successiva"
              >›</button>
            )}
            <button
              onClick={() => setLightbox(null)}
              style={{ position: 'absolute', top: '-40px', right: 0, background: 'none', border: 'none', color: '#fff', fontSize: '2rem', cursor: 'pointer', lineHeight: 1 }}
              aria-label="Chiudi"
            >×</button>
            {lightbox?.photos?.length > 1 && (
              <div style={{ position: 'absolute', bottom: '-32px', left: '50%', transform: 'translateX(-50%)', color: 'rgba(255,255,255,0.7)', fontSize: '0.875rem' }}>
                {lightbox?.currentIndex + 1} / {lightbox?.photos?.length}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceRequestDetailPage;